import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useApp } from '../../contexts/AppContext';

const SpecialEvaluationExecution = () => {
  const { projectId, subjectId, evaluationType } = useParams();
  const navigate = useNavigate();
  const { showSuccess, showError } = useApp();

  const [students, setStudents] = useState([]);
  const [indicators, setIndicators] = useState([]);
  const [evaluations, setEvaluations] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadEvaluationData();
  }, [projectId, subjectId, evaluationType]);

  const loadEvaluationData = async () => {
    try {
      setLoading(true);

      // Mock indicadores configurados para esta evaluación
      const mockIndicators = [
        { id: 'ind1', title: 'Participación activa', type: 'proceso' },
        { id: 'ind2', title: 'Trabajo colaborativo', type: 'proceso' },
        { id: 'ind3', title: 'Responsabilidad', type: 'producto' },
        { id: 'ind4', title: 'Comunicación efectiva', type: 'producto' },
      ];

      // Mock estudiantes del proyecto
      const mockStudents = [
        { id: 1, firstName: 'Juan', lastName: 'González' },
        { id: 2, firstName: 'María', lastName: 'Fernández' },
        { id: 3, firstName: 'Pedro', lastName: 'Martínez' },
        { id: 4, firstName: 'Ana', lastName: 'López' },
        { id: 5, firstName: 'Carlos', lastName: 'Rodríguez' },
        { id: 6, firstName: 'Laura', lastName: 'Sánchez' },
        { id: 7, firstName: 'Diego', lastName: 'Torres' },
        { id: 8, firstName: 'Sofía', lastName: 'Ramírez' },
      ];

      // Ordenar estudiantes alfabéticamente por apellido
      const sortedStudents = mockStudents.sort((a, b) =>
        a.lastName.localeCompare(b.lastName)
      );

      setIndicators(mockIndicators);
      setStudents(sortedStudents);

      // Inicializar evaluaciones vacías
      const initialEvaluations = {};
      sortedStudents.forEach(student => {
        mockIndicators.forEach(indicator => {
          initialEvaluations[`${student.id}-${indicator.id}`] = '';
        });
      });
      setEvaluations(initialEvaluations);

    } catch (error) {
      showError('Error al cargar los datos de evaluación');
    } finally {
      setLoading(false);
    }
  };

  const getEvaluationTitle = () => {
    return evaluationType === 'self-evaluation' ? 'Autoevaluación' : 'Coevaluación';
  };

  const getEvaluationIcon = () => {
    if (evaluationType === 'self-evaluation') {
      return (
        <svg className="w-8 h-8 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    }
    return (
      <svg className="w-8 h-8 text-purple-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    );
  };

  const handleEvaluationChange = (studentId, indicatorId, value) => {
    // Validar que el valor esté entre 1 y 5 o vacío
    if (value !== '' && (isNaN(value) || value < 1 || value > 5)) {
      return;
    }

    const key = `${studentId}-${indicatorId}`;
    setEvaluations({
      ...evaluations,
      [key]: value
    });
  };

  const getScoreColor = (score) => {
    if (!score || score === '') return 'bg-white';

    const numScore = parseInt(score);
    if (numScore === 5) return 'bg-green-100';
    if (numScore === 3 || numScore === 4) return 'bg-yellow-100';
    if (numScore === 1 || numScore === 2) return 'bg-red-100';

    return 'bg-white';
  };

  const calculateStudentTotal = (studentId, type = null) => {
    let total = 0;
    const filteredIndicators = type
      ? indicators.filter(ind => ind.type === type)
      : indicators;

    filteredIndicators.forEach(indicator => {
      const key = `${studentId}-${indicator.id}`;
      const score = evaluations[key];
      if (score !== '' && !isNaN(score)) {
        total += parseInt(score);
      }
    });
    return total;
  };

  const handleSaveEvaluations = () => {
    // Aquí se guardaría en la API
    showSuccess('Evaluaciones guardadas correctamente');
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    );
  }

  const themeColor = evaluationType === 'self-evaluation' ? 'blue' : 'purple';

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate(`/projects/${projectId}?tab=evaluations`)}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Volver a evaluaciones
          </button>

          <div className={`bg-gradient-to-br from-${themeColor}-50 to-${themeColor}-100 rounded-xl shadow-sm border border-${themeColor}-200 p-6`}>
            <div className="flex items-center space-x-4 mb-3">
              {getEvaluationIcon()}
              <h1 className="text-2xl font-bold text-gray-900">
                Ejecución de {getEvaluationTitle()}
              </h1>
            </div>
            <p className="text-gray-700 font-medium">
              {getEvaluationTitle()}
            </p>
            <p className="text-sm text-gray-600 mt-2">
              Ingresa las calificaciones de 1 a 5 para cada estudiante en cada indicador
            </p>
          </div>
        </div>

        {/* Tabla de Evaluación */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {/* Fila de agrupación Proceso/Producto */}
                <tr>
                  <th
                    scope="col"
                    rowSpan={2}
                    className="sticky left-0 z-10 bg-gray-50 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-r border-gray-200"
                    style={{ minWidth: '200px' }}
                  >
                    Estudiante
                  </th>
                  {indicators.filter(ind => ind.type === 'proceso').length > 0 && (
                    <th
                      scope="col"
                      colSpan={indicators.filter(ind => ind.type === 'proceso').length}
                      className="px-4 py-2 text-center text-xs font-semibold text-orange-700 uppercase tracking-wider bg-orange-50 border-x border-gray-300"
                    >
                      Proceso
                    </th>
                  )}
                  <th
                    scope="col"
                    rowSpan={2}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-100 border-l border-gray-300"
                    style={{ minWidth: '100px' }}
                  >
                    Total<br/>Proceso
                  </th>
                  {indicators.filter(ind => ind.type === 'producto').length > 0 && (
                    <th
                      scope="col"
                      colSpan={indicators.filter(ind => ind.type === 'producto').length}
                      className="px-4 py-2 text-center text-xs font-semibold text-green-700 uppercase tracking-wider bg-green-50 border-x border-gray-300"
                    >
                      Producto
                    </th>
                  )}
                  <th
                    scope="col"
                    rowSpan={2}
                    className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-100 border-l border-gray-300"
                    style={{ minWidth: '100px' }}
                  >
                    Total<br/>Producto
                  </th>
                </tr>
                {/* Fila de Indicadores individuales */}
                <tr>
                  {indicators.filter(ind => ind.type === 'proceso').map((indicator) => (
                    <th
                      key={indicator.id}
                      scope="col"
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-orange-50 border-x border-gray-200"
                      style={{ minWidth: '150px' }}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-bold text-xs line-clamp-2">
                          {indicator.title}
                        </span>
                      </div>
                    </th>
                  ))}
                  {indicators.filter(ind => ind.type === 'producto').map((indicator) => (
                    <th
                      key={indicator.id}
                      scope="col"
                      className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider bg-green-50 border-x border-gray-200"
                      style={{ minWidth: '150px' }}
                    >
                      <div className="flex flex-col items-center">
                        <span className="font-bold text-xs line-clamp-2">
                          {indicator.title}
                        </span>
                      </div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {students.map((student, index) => {
                  const totalProceso = calculateStudentTotal(student.id, 'proceso');
                  const totalProducto = calculateStudentTotal(student.id, 'producto');
                  return (
                    <tr key={student.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="sticky left-0 z-10 px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border-r border-gray-200 bg-inherit">
                        {student.lastName}, {student.firstName}
                      </td>
                      {/* Inputs de Proceso */}
                      {indicators.filter(ind => ind.type === 'proceso').map((indicator) => {
                        const key = `${student.id}-${indicator.id}`;
                        const score = evaluations[key];
                        return (
                          <td key={indicator.id} className="px-4 py-4 whitespace-nowrap text-center bg-orange-50/30">
                            <input
                              type="number"
                              min="1"
                              max="5"
                              value={score}
                              onChange={(e) => handleEvaluationChange(student.id, indicator.id, e.target.value)}
                              className={`w-16 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${getScoreColor(score)}`}
                              placeholder="-"
                            />
                          </td>
                        );
                      })}
                      {/* Total Proceso */}
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-orange-900 bg-orange-100 border-l border-gray-300">
                        {totalProceso}
                      </td>
                      {/* Inputs de Producto */}
                      {indicators.filter(ind => ind.type === 'producto').map((indicator) => {
                        const key = `${student.id}-${indicator.id}`;
                        const score = evaluations[key];
                        return (
                          <td key={indicator.id} className="px-4 py-4 whitespace-nowrap text-center bg-green-50/30">
                            <input
                              type="number"
                              min="1"
                              max="5"
                              value={score}
                              onChange={(e) => handleEvaluationChange(student.id, indicator.id, e.target.value)}
                              className={`w-16 px-3 py-2 text-center border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent transition-colors ${getScoreColor(score)}`}
                              placeholder="-"
                            />
                          </td>
                        );
                      })}
                      {/* Total Producto */}
                      <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-bold text-green-900 bg-green-100 border-l border-gray-300">
                        {totalProducto}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        {/* Botones de acción */}
        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="font-medium">{students.length}</span> estudiantes •
            <span className="font-medium ml-1">{indicators.length}</span> indicadores de evaluación
          </div>
          <div className="flex space-x-4">
            <button
              onClick={() => navigate(`/projects/${projectId}?tab=evaluations`)}
              className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Cancelar
            </button>
            <button
              onClick={handleSaveEvaluations}
              className="bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
            >
              Guardar Evaluaciones
            </button>
          </div>
        </div>

        {/* Leyenda de colores */}
        <div className="mt-6 bg-white rounded-xl shadow-sm border border-gray-200 p-4">
          <h3 className="text-sm font-semibold text-gray-700 mb-3">Escala de Evaluación:</h3>
          <div className="flex flex-wrap gap-4">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-green-100 border border-gray-300 rounded"></div>
              <span className="text-sm text-gray-600">Excelente (5)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-yellow-100 border border-gray-300 rounded"></div>
              <span className="text-sm text-gray-600">Bueno/Satisfactorio (3-4)</span>
            </div>
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 bg-red-100 border border-gray-300 rounded"></div>
              <span className="text-sm text-gray-600">Insuficiente (1-2)</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SpecialEvaluationExecution;
